import React, { useEffect, useLayoutEffect, useState, memo, useCallback } from 'react';
import { View, Pressable, Platform, StyleSheet, Modal, TouchableOpacity, ScrollView, Text } from 'react-native';
import { FontAwesome5, Ionicons, MaterialIcons } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useWindowDimensions } from 'react-native';
import styles from '@stylesheet';
import searchStyles from '../searchStyles';
import { GenerateArrayTypes } from '../Types';
import { Popover } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDeviceType } from '@hooks/useDeviceType';

const AnimatedIcon = ({ name }) => {
  const theme = themeSelector();
  return (
    <Animatable.View 
      useNativeDriver 
      animation="fadeInDown" 
      duration={250} 
      style={modernStyles.animatedIconContainer}
    >
      <FontAwesome5 
        name={name} 
        size={name === 'ellipsis-h' ? 20 : 18} 
        color={theme ? "rgba(100, 116, 139, 0.8)" : "rgba(148, 163, 184, 0.8)"} 
        style={modernStyles.animatedIcon}
      />
    </Animatable.View>
  );
};

const IconItem = ({ item, index, handleFunction, setExtendedMenu, extendedMenu, showLabel = false }) => {
  const theme = themeSelector();
  
  return (
    <View style={{ alignItems: 'center', maxWidth: showLabel ? 80 : 60, marginBottom: showLabel ? 8 : 0 }}>
      <Pressable
        style={({ pressed }) => [
          modernStyles.iconButton,
          {
            backgroundColor: theme ? "#ffffffff" : "#1e293b",
            borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
            shadowColor: theme ? "#000" : "#fff",
            shadowOpacity: theme ? 0.08 : 0.15,
            transform: [{ scale: pressed ? 0.95 : 1 }],
            width: showLabel ? 64 : 48,
            height: showLabel ? 64 : 48,
          }
        ]}
        key={item.functionName}
        onPress={() => {
          handleFunction(item.function());
          if (item.extendedOptions && extendedMenu !== item.functionName) {
            setExtendedMenu(item.functionName);
          }
        }}
        accessibilityRole="button"
        accessibilityLabel={item.functionName || 'Action'}
      >
        {({ hovered }) => (
          <Animatable.View 
            useNativeDriver 
            transition={'backgroundColor'} 
            style={[
              modernStyles.iconButtonContent,
              { 
                backgroundColor: hovered 
                  ? (theme ? "rgba(99, 102, 241, 0.1)" : "rgba(139, 92, 246, 0.15)")
                  : "transparent"
              }
            ]}
          >
            <View style={[modernStyles.iconWrapper, {
              backgroundColor: theme ? "#4A90E2" : "#374151",
              width: showLabel ? 48 : 32,
              height: showLabel ? 48 : 32,
            }]}>
              {item.icon}
            </View>
          </Animatable.View>
        )}
      </Pressable>
      {showLabel && item.functionName && (
        <Text
          style={{
            marginTop: 6,
            fontSize: 11,
            color: theme ? '#374151' : '#d1d5db',
            textAlign: 'center',
            maxWidth: 70,
          }}
          numberOfLines={2}
        >
          {item.functionName.replace(/([A-Z])/g, ' $1').trim()}
        </Text>
      )}
    </View>
  );
};

const GenerateArray = ({
  customIcons,
  setExtendedMenu,
  extendedMenu,
  disableBurger,
  setShowExtendedMenu,
  customIconStyle,
  width,
  extraComponents,
  otherComponents
}: GenerateArrayTypes) => {
  // Calculate actual icon size for breakpoint calculation
  const iconSize = 48;
  const iconGap = 12;
  const iconCount = customIcons?.length || 0;
  const sizeOfArray = iconCount > 0 ? iconCount * (iconSize + iconGap) + iconGap : 0; // Actual size needed
  const theme = themeSelector();
  const { isTablet } = useDeviceType();
  const dimensions = useWindowDimensions();
  const [openMenu, setOpenMenu] = useState(false);

  // Memoize breakpoint calculation
  const breakpointAchieved = React.useMemo(() => {
    // If width is provided, check if icons exceed that width
    // If no width, check if icons exceed 50% of screen width
    return width ? sizeOfArray > width : sizeOfArray > (dimensions.width * 0.5);
  }, [width, sizeOfArray, dimensions.width]);

  const handleFunction = useCallback((passedFunction) => {
    setOpenMenu(false);
    if (typeof passedFunction === 'function') {
      passedFunction();
    }
  }, []);

  // Memoize icon rendering function
  const renderIcons = useCallback((showLabel = false) => {
    if (!customIcons || customIcons.length === 0) return null;
    return customIcons.map((item, index) => (
      <IconItem
        key={item.functionName}
        item={item}
        index={index}
        handleFunction={handleFunction}
        setExtendedMenu={setExtendedMenu}
        extendedMenu={extendedMenu}
        showLabel={showLabel}
      />
    ));
  }, [customIcons, handleFunction, extendedMenu]);

  // Memoize other components rendering
  const renderOtherComponentsInModal = useCallback((showLabel = false) => {
    if (!otherComponents || otherComponents.length === 0) return null;

    return otherComponents.map((item, index) => (
      <IconItem
        key={`other-${item.functionName}-${index}`}
        item={item}
        index={index}
        handleFunction={handleFunction}
        setExtendedMenu={setExtendedMenu}
        extendedMenu={extendedMenu}
        showLabel={showLabel}
      />
    ));
  }, [otherComponents, handleFunction, extendedMenu]);

  // Determine if burger should be shown
  const showBurger = breakpointAchieved && !isTablet && Platform.OS !== 'web' && !disableBurger;

  // Calculate actual icon container width
  // Use the sizeOfArray we already calculated above
  const calculatedWidth = sizeOfArray;
  
  // Use provided width if available, but ensure it's at least the calculated width
  // If provided width is smaller than needed, breakpointAchieved will trigger burger menu
  // Otherwise, use the calculated width to ensure all icons fit
  const containerWidth = width ? Math.max(width, calculatedWidth) : calculatedWidth;

  // Memoize container style
  const containerStyle = React.useMemo(() => [
    modernStyles.iconsContainer,
    showBurger && Platform.OS !== 'web' ? {
      // On mobile with burger, make container narrow and align to end
      width: 60,
      maxWidth: 60,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      marginRight: 0, // Remove default marginRight
      paddingRight: 8,
    } : !showBurger && {
    // Normal icon array - use calculated or provided width
      width: containerWidth,
      maxWidth: containerWidth,
      minWidth: containerWidth,
      marginRight: 0, // Remove default margin to prevent overlap
    },
    customIconStyle as any
  ], [showBurger, isTablet, customIcons?.length || 0, customIconStyle, containerWidth, width]);

  return (
    <View style={[
      searchStyles.iconArrayStyle,
      modernStyles.arrayContainer,
      showBurger && { alignItems: 'flex-end' }
    ]}>
      <View style={containerStyle}>
        {showBurger ? (
          <BurgerComponent
            customIcons={customIcons}
            renderIcons={renderIcons}
            renderOtherComponents={renderOtherComponentsInModal}
            extraComponents={extraComponents}
            otherComponents={otherComponents}
            openMenu={openMenu}
            setOpenMenu={setOpenMenu}
          />
        ) : (
            renderIcons()
        )}
      </View>
    </View>
  );
};

const BurgerComponent = ({ customIcons, renderIcons, renderOtherComponents, extraComponents, otherComponents, openMenu, setOpenMenu }) => {
  const theme = themeSelector();
  
  // Use Modal for native, Popover for web
  if (Platform.OS === 'web') {
    return (
      <View style={[
        styles.centerEverything,
        modernStyles.burgerContainer
      ]}>
        <Popover
          stayInFrame
          placement='bottom'
          open={openMenu}
          onOpenChange={setOpenMenu}
        >
          <Popover.Trigger asChild>
            <Animatable.View
              animation="pulse"
              iterationCount="infinite"
              iterationDelay={2000}
              duration={1500}
            >
              <Pressable
                hitSlop={15}
                onPress={() => setOpenMenu(!openMenu)}
                style={({ pressed }) => [
                  modernStyles.burgerButton,
                  {
                    backgroundColor: theme ? "#ffffffff" : "#1e293b",
                    borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
                    shadowColor: theme ? "#000" : "#fff",
                    shadowOpacity: theme ? 0.1 : 0.2,
                    transform: [{ scale: pressed ? 0.9 : 1 }],
                  }
                ]}
                accessibilityRole="button"
                accessibilityLabel="More options"
                accessibilityState={{ expanded: openMenu }}
              >
                <View style={[modernStyles.burgerIconContainer, {
                  backgroundColor: theme ? "#f8fafc" : "#334155"
                }]}>
                  <MaterialIcons
                    name="more-vert"
                    size={18}
                    color={theme ? "#6366f1" : "#8b5cf6"}
                  />
                </View>
              </Pressable>
            </Animatable.View>
          </Popover.Trigger>
          <Popover.Content
            enterStyle={{ y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ y: -10, opacity: 0, scale: 0.95 }}
            animation={[
              'superBouncy',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
          >
            <Popover.Arrow
              borderColor={theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)"}
              backgroundColor={theme ? "#ffffff" : "#1e293b"}
            />
            <View style={[modernStyles.popoverContent, {
              backgroundColor: theme ? "#ffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)"
            }]}>
              {renderOtherComponents && renderOtherComponents(true)}
              {renderIcons(true)}
              {extraComponents && (
                <View style={{ marginTop: 8, width: '100%' }}>
                  {extraComponents}
                </View>
              )}
            </View>
          </Popover.Content>
        </Popover>
      </View>
    );
  }

  // Native implementation with Modal
  return (
    <View style={[
      styles.centerEverything,
      modernStyles.burgerContainer
    ]}>
      <Animatable.View
        animation="pulse"
        iterationCount="infinite"
        iterationDelay={2000}
        duration={1500}
      >
        <Pressable
          hitSlop={15}
          onPress={() => setOpenMenu(true)}
          style={({ pressed }) => [
            modernStyles.burgerButton,
            {
              backgroundColor: theme ? "#ffffffff" : "#1e293b",
              borderColor: theme ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.1)",
              shadowColor: theme ? "#000" : "#fff",
              shadowOpacity: theme ? 0.1 : 0.2,
              transform: [{ scale: pressed ? 0.9 : 1 }],
            }
          ]}
          accessibilityRole="button"
          accessibilityLabel="More options"
          accessibilityState={{ expanded: openMenu }}
        >
          <View style={[modernStyles.burgerIconContainer, {
            backgroundColor: theme ? "#f8fafc" : "#334155"
          }]}>
            <MaterialIcons
              name="more-vert"
              size={18}
              color={theme ? "#6366f1" : "#8b5cf6"}
            />
          </View>
        </Pressable>
      </Animatable.View>

      <Modal
        visible={openMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setOpenMenu(false)}
      >
        <TouchableOpacity
          style={modernStyles.modalOverlay}
          activeOpacity={1}
          onPress={() => setOpenMenu(false)}
        >
          <Animatable.View
            animation="zoomIn"
            duration={300}
            useNativeDriver
            style={[
              modernStyles.modalContent,
              {
                backgroundColor: theme ? "#ffffff" : "#1e293b",
                borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)",
              }
            ]}
          >
            <View style={modernStyles.modalHeader}>
              <View style={modernStyles.modalHandle} />
            </View>
            <ScrollView
              style={modernStyles.modalScrollView}
              contentContainerStyle={modernStyles.modalIconsContainer}
              showsVerticalScrollIndicator={false}
            >

              {renderOtherComponents && renderOtherComponents(true)}
              {renderIcons(true)}
              {extraComponents && (
                <View style={{ width: '100%', marginTop: 12, padding: 12 }}>
                  {extraComponents}
                </View>
              )}

              {/* Fallback content if nothing renders */}
              {!customIcons?.length && !extraComponents && (
                <View style={{ padding: 20, alignItems: 'center' }}>
                  <MaterialIcons name="info-outline" size={48} color={theme ? '#999' : '#666'} />
                  <Text style={{ marginTop: 8, color: theme ? '#666' : '#999', fontSize: 14 }}>
                    No actions available
                  </Text>
                </View>
              )}
            </ScrollView>
            <Pressable
              style={[modernStyles.closeButton, {
                backgroundColor: theme ? "#f3f4f6" : "#374151"
              }]}
              onPress={() => setOpenMenu(false)}
              accessibilityRole="button"
              accessibilityLabel="Close menu"
            >
              <MaterialIcons
                name="close"
                size={24}
                color={theme ? "#1f2937" : "#f9fafb"}
              />
            </Pressable>
          </Animatable.View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const modernStyles = StyleSheet.create({
  // Container Styles
  arrayContainer: {
    flexGrow: 1,
    alignSelf: "center",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  iconsContainer: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'nowrap', // Prevent wrapping
    overflow: 'visible', // Allow icons to be visible
  },

  // Animated Icon Styles
  animatedIconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },

  animatedIcon: {
    textAlign: 'center',
  },

  // Icon Button Styles
  iconButton: {
    minWidth: 48,
    minHeight: 48,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 4 },
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(10px)',
      }
    })
  },

  iconButtonContent: {
    borderRadius: 14,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },

  iconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Burger Component Styles
  burgerContainer: {
    maxWidth: 40,
    minWidth: 40,
    alignContent: 'center',
    alignItems: 'flex-end',
    justifyContent: 'center',
    alignSelf: 'flex-end',
  },

  burgerButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
    elevation: 2,
    borderWidth: 1,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(8px)',
      }
    })
  },

  burgerIconContainer: {
    width: 24,
    height: 24,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },

  popoverContent: {
    gap: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    borderRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 20,
    elevation: 12,
    borderWidth: 1,
    minWidth: 200,
    maxWidth: 320,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(20px)',
      }
    })
  },

  // Native Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 9999,
  },
  modalContent: {
    borderRadius: 24,
    borderWidth: 1,
    width: '90%',
    maxHeight: '50%',
    minHeight: 200,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 16,
    zIndex: 10000,
  },
  modalHeader: {
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  modalHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: 'rgba(128, 128, 128, 0.3)',
  },
  modalScrollView: {
    flex: 1,
    maxHeight: 200,
  },
  modalIconsContainer: {
    padding: 16,
    gap: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    margin: 8,
    padding: 12,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default memo(GenerateArray);
